<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiCard, UiIcon, UiSquareButton, UiSwitch } from "@shopexperts/shared";
import type { Redirect } from "@shopexperts/shared";

interface RedirectCardProps {
  redirect: Redirect;
}

const props = defineProps<RedirectCardProps>();

const emit = defineEmits<{
  (e: 'edit'): void;
  (e: 'delete'): void;
  (e: 'toggle-active'): void;
}>();

const formattedCreatedDate = computed(() => {
  if (!props.redirect.created_at) return 'N/A';
  const date = new Date(props.redirect.created_at);
  return date.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
});

const formattedUpdatedDate = computed(() => {
  if (!props.redirect.updated_at) return 'N/A';
  const date = new Date(props.redirect.updated_at);
  return date.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
});

const formattedExpiresDate = computed(() => {
  if (!props.redirect.expires_at) return null;
  const date = new Date(props.redirect.expires_at);
  return date.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
});

const statusCodeClass = computed(() => {
  return props.redirect.status_code === 301 
    ? 'bg-blue-100 text-blue-700' 
    : 'bg-amber-100 text-amber-700';
});

const statusCodeLabel = computed(() => {
  return props.redirect.status_code === 301 
    ? '301 Permanent' 
    : '302 Temporary';
});

const activeLabel = computed(() => {
  if (!props.redirect.is_active) return 'Inactive';
  if (props.redirect.is_expired) return 'Expired';
  if (!props.redirect.is_effective) return 'Not Effective';
  return 'Active';
});

const activeSwatchVariant = computed(() => {
  if (!props.redirect.is_active) return 'muted';
  if (props.redirect.is_expired) return 'danger';
  if (!props.redirect.is_effective) return 'warning';
  return 'success';
});

const activeLabelClass = computed(() => {
  if (!props.redirect.is_active) return 'text-gray-500';
  if (props.redirect.is_expired) return 'text-se-danger';
  if (!props.redirect.is_effective) return 'text-amber-700';
  return 'text-se-success';
});

// Optimistic local state so the switch toggles instantly while the parent persists the change.
const localIsActive = ref(props.redirect.is_active);
watch(
  () => props.redirect.is_active,
  (next) => {
    localIsActive.value = next;
  }
);

function onEditClick() {
  emit('edit');
}

function onDeleteClick() {
  emit('delete');
}

function onToggleActiveClick() {
  emit('toggle-active');
}
</script>

<template>
  <UiCard padding="md" class="hover:shadow-md transition-shadow">
    <div class="flex items-start justify-between gap-6">
      <!-- Redirect Info -->
      <div class="flex-1 min-w-0">
        <!-- Header: Source -> Target Path -->
        <div class="flex items-center gap-3 mb-2 flex-wrap">
          <code class="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
            {{ redirect.source_path }}
          </code>
          <UiIcon name="arrow-right" class="w-4 h-4 text-gray-400 flex-shrink-0" />
          <code class="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-gray-800 break-all">
            {{ redirect.target_path }}
          </code>
        </div>

        <!-- Badges Row -->
        <div class="flex items-center gap-2 mb-3 flex-wrap">
          <span :class="[statusCodeClass, 'px-2 py-0.5 rounded text-xs font-medium']">
            {{ statusCodeLabel }}
          </span>
          <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-white border border-gray-200">
            <UiSwatch :variant="activeSwatchVariant" size="xs" :label="activeLabel" />
            <span :class="activeLabelClass">{{ activeLabel }}</span>
          </span>
        </div>

        <!-- Stats Row -->
        <div class="flex items-center gap-6 text-sm flex-wrap">
          <!-- Hits Count -->
          <div class="flex items-center gap-1.5">
            <UiIcon name="play" class="w-4 h-4 text-gray-400" />
            <span class="text-gray-600">{{ redirect.hits_count }} hits</span>
          </div>

          <!-- Created Date -->
          <div class="flex items-center gap-1.5">
            <span class="text-gray-400">Created:</span>
            <span class="text-gray-600">{{ formattedCreatedDate }}</span>
          </div>

          <!-- Updated Date -->
          <div class="flex items-center gap-1.5">
            <span class="text-gray-400">Updated:</span>
            <span class="text-gray-600">{{ formattedUpdatedDate }}</span>
          </div>

          <!-- Expires Date -->
          <div v-if="formattedExpiresDate" class="flex items-center gap-1.5">
            <span class="text-gray-400">Expires:</span>
            <span :class="redirect.is_expired ? 'text-red-600' : 'text-gray-600'">
              {{ formattedExpiresDate }}
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <UiSwitch
          v-model="localIsActive"
          size="sm"
          class="mr-1"
          :title="localIsActive ? 'Disable redirect' : 'Enable redirect'"
          @update:model-value="onToggleActiveClick"
        />
        <UiSquareButton 
          icon="pencil" 
          variant="ghost"
          title="Edit redirect"
          @click="onEditClick"
        />
        <UiSquareButton 
          icon="delete" 
          variant="ghost"
          class="hover:text-red-600"
          title="Delete redirect"
          @click="onDeleteClick"
        />
      </div>
    </div>
  </UiCard>
</template>

